import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  CssBaseline,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import "../../Styles/Custodians.css";
import { useDispatch, useSelector } from "react-redux";
import { getCustodians, getAllInvestorsWithStrategies } from "../../Redux/Actions/strategyService";
import { getOrders } from "../../Redux/Actions/transactionService";
import Loader from "../../Loader";

// Import icons for the buttons
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Import modal components
import {
  UploadModal,
  DownloadModal,
  PreviewModal,
  SelectTradesModal,
  UploadSuccessModal,
  UploadErrorModal
} from './Modals';

function Index() {
  const dispatch = useDispatch();
  const [CustodianData, setCustodianData] = useState([]);
  const [selectedCustodian, setSelectedCustodian] = useState(null);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openDownloadModal, setOpenDownloadModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openSelectTradesModal, setOpenSelectTradesModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedDownloadOption, setSelectedDownloadOption] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedTradeStatus, setSelectedTradeStatus] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // 'list' or 'calendar'

  const [selectedTrades, setSelectedTrades] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStrategy, setFilterStrategy] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [ordersData, setOrdersData] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [strategiesData, setStrategiesData] = useState([]);
  
  // New state for import/export details modals
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [openImportDetailsModal, setOpenImportDetailsModal] = useState(false);
  const [openExportDetailsModal, setOpenExportDetailsModal] = useState(false);
  const [importDetailsData, setImportDetailsData] = useState(null);
  const [exportDetailsData, setExportDetailsData] = useState(null);



  // Effect to ensure modal opens when previewData is set
  useEffect(() => {
    if (previewData && !openPreviewModal) {
      setOpenPreviewModal(true);
    } 
  }, [previewData, openPreviewModal]);



  const { Custodians, loading, strategiesWithInvestors } = useSelector((state) => state.strategy);

  useEffect(() => {
    dispatch(getCustodians());
    // Fetch strategies data for CHARLES SCHWAB custodian
    dispatch(getAllInvestorsWithStrategies({ custodian: 'CHARLES SCHWAB' }));
  }, []);

  useEffect(() => {
    if (Custodians && Custodians.length > 0) {
      setCustodianData(Custodians);
    } else {
      setCustodianData([]);
    }
  }, [Custodians]);


  // Debug function to test matching logic
  const debugMatchingLogic = () => {
    
    if (ordersData && ordersData.length > 0 && strategiesWithInvestors && strategiesWithInvestors.length > 0) {
     
      ordersData.forEach((order, index) => {
        const orderStrategyId = order.strategy?.id;
       
        
        const matchingStrategy = strategiesWithInvestors.find(s => s.strategy?.id === orderStrategyId);
       
      });
    }
  };

  // Call debug function when both datasets are available
  useEffect(() => {
    if (ordersData && ordersData.length > 0 && strategiesWithInvestors && strategiesWithInvestors.length > 0) {
      debugMatchingLogic();
    }
  }, [ordersData, strategiesWithInvestors]);

  // Test function for trade status filtering
  const testTradeStatusFiltering = () => {
   
    const safeOrdersData = Array.isArray(ordersData) ? ordersData : [];
    
    const testStatuses = ['pending', 'completed', 'failed', 'cancelled', 'PENDING', 'COMPLETED', 'FAILED', 'CANCELLED'];
    
    testStatuses.forEach(testStatus => {
      const filtered = safeOrdersData.filter(order => 
        order.status?.toLowerCase() === testStatus.toLowerCase()
      );
     
    });
    
    // Test the exact filtering logic used in the app
    const testSelectedStatus = 'completed';
    const filtered = safeOrdersData.filter(order => {
      const orderStatus = order.status?.toLowerCase();
      const selectedStatus = testSelectedStatus.toLowerCase();
      const isIncluded = orderStatus === selectedStatus;
      return isIncluded;
    });
  };

  // Call test function when orders data is available
  useEffect(() => {
    if (ordersData && ordersData.length > 0) {
      testTradeStatusFiltering();
    }
  }, [ordersData]);

  // Manual test function for trade status filtering
  const testTradeStatusFilteringManual = () => {
  
    const safeOrdersData = Array.isArray(ordersData) ? ordersData : [];
   
    
    // Test with 'completed' status
    const testStatus = 'completed';
    const filtered = safeOrdersData.filter(order => {
      const orderStatus = order.status?.toLowerCase();
      const selectedStatus = testStatus.toLowerCase();
      const isIncluded = orderStatus === selectedStatus;
     
      return isIncluded;
    });
  
    return filtered;
  };

  // Make test function available globally for debugging
  window.testTradeStatusFilteringManual = testTradeStatusFilteringManual;

  // Test function to verify the exact data structure
  const testDataStructure = () => {
   
  };

  // Make test function available globally for debugging
  window.testDataStructure = testDataStructure;

  const theme = createTheme();

  const handleCustodianClick = (custodian) => {
    setSelectedCustodian(custodian);
  };

  const handleBackClick = () => {
    setSelectedCustodian(null);
  };

  const handleImport = (custodian) => {
    setOpenUploadModal(true);
  };

  const handleExport = async (custodian) => {
    try {
      setIsLoadingOrders(true);
      
      // Fetch orders for the selected custodian with pending status
      const result = await dispatch(getOrders({
        custodian: 'CHARLES SCHWAB', // Fixed custodian name as per API requirement
        status: 'PENDING',
        sort: 'desc',
        take: 1000 // Get more records for filtering
      }));
      
    
      
      // Handle the specific API response structure
      let orders = [];
      
      if (result.payload && result.payload.data && result.payload.data.orders) {
        orders = result.payload.data.orders;
       
      } 
      
      setOrdersData(orders);
      setFilteredOrders(orders);
      
      setOpenDownloadModal(true);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrdersData([]);
      setFilteredOrders([]);
      setOpenDownloadModal(true);
    } finally {
      setIsLoadingOrders(false);
    }
  };

  const handleCloseUploadModal = () => {
    setOpenUploadModal(false);
  };

  const handleCloseDownloadModal = () => {
    setOpenDownloadModal(false);
    setSelectedDownloadOption('');
    setSelectedTrades([]); // Reset selected trades when closing modal
  };

  const handleDownloadOptionSelect = (option) => {
    setSelectedDownloadOption(option);
    // Reset all form values when changing options
    setSelectedDate('');
    setStartDate('');
    setEndDate('');
    setSelectedTradeStatus('');
    setSelectedTrades([]); // Reset selected trades when switching options
    
    // For Download Specific Trades, open the SelectTradesModal
    if (option === 'Download Specific Trades') {
      setOpenDownloadModal(false);
      setOpenSelectTradesModal(true);
    }
  };



  const handleBackToDownload = () => {

    setOpenPreviewModal(false);
    setPreviewData(null);
    
    // Use setTimeout to ensure state changes are processed before opening download modal
    setTimeout(() => {
    
      setOpenDownloadModal(true);
     
    }, 100); // Small delay to ensure state changes are processed
  };

  const handleClosePreviewModal = () => {
    setOpenPreviewModal(false);
    setPreviewData(null);
    setSelectedDownloadOption('');
    setSelectedDate('');
    setStartDate('');
    setEndDate('');
    setSelectedTradeStatus('');
  };

  const handleCloseSelectTradesModal = () => {
    setOpenSelectTradesModal(false);
    setSelectedTrades([]);
    setSearchQuery('');
    setFilterStrategy('');
    setFilterStatus('');
    setSortBy('');
  };

  const handleTradeSelection = (tradeIndex) => {
    setSelectedTrades(prev => {
      if (prev.includes(tradeIndex)) {
        return prev.filter(index => index !== tradeIndex);
      } else {
        return [...prev, tradeIndex];
      }
    });
  };

  const handleSelectAllTrades = () => {
    const safeOrdersData = Array.isArray(ordersData) ? ordersData : [];
    // Filter for BUY/SELL actions only and exclude MANAGER_DISCRETION
    const filteredOrdersData = safeOrdersData.filter(order => 
      order.type !== 'MANAGER_DISCRETION' && 
      (order.action === 'BUY' || order.action === 'SELL')
    );
    const dataToUse = filteredOrdersData.length > 0 ? transformOrdersForPreview(filteredOrdersData) : mockTradeData;
    if (selectedTrades.length === dataToUse.length) {
      setSelectedTrades([]);
    } else {
      setSelectedTrades(dataToUse?.map((_, index) => index));
    }
  };

  // New handler functions for import/export details modals
  const handleRowSelection = (rowData) => {
    setSelectedRow(rowData);
    setSelectedCalendarDate(null); // Clear calendar selection when row is selected
  };

  const handleCalendarDateSelection = (date) => {
    setSelectedCalendarDate(date);
    setSelectedRow(null); // Clear row selection when calendar date is selected
  };

  const handleImportDetailsClick = (rowData) => {
    // Get the selected date from either row data or calendar selection
    const selectedDate = rowData?.date || selectedCalendarDate;
    
    // Format the date properly for display
    const formatDateForDisplay = (date) => {
      if (typeof date === 'number') {
        // Calendar date is a number (1-31), format as "2025/09/XX"
        return `2025/09/${date.toString().padStart(2, '0')}`;
      }
      if (typeof date === 'string' && date.includes('/')) {
        // Table row date is in "MM/DD/YYYY" format, convert to "YYYY/MM/DD"
        const [month, day, year] = date.split('/');
        return `${year}/${month}/${day}`;
      }
      return date || "2025/09/21";
    };

    // Mock data for import details - replace with actual API call
    const importData = {
      date: formatDateForDisplay(selectedDate),
      dateImported: "15/05/2025, 17:48:30",
      totalFiles: "2",
      fileTypes: [".xls", ".csv"],
      missingFiles: [".tsv"]
    };
    setImportDetailsData(importData);
    setOpenImportDetailsModal(true);
  };

  const handleExportDetailsClick = (rowData) => {
    // Get the selected date from either row data or calendar selection
    const selectedDate = rowData?.date || selectedCalendarDate;
    
    // Format the date properly for display
    const formatDateForDisplay = (date) => {
      if (typeof date === 'number') {
        // Calendar date is a number (1-31), format as "2025/09/XX"
        return `2025/09/${date.toString().padStart(2, '0')}`;
      }
      if (typeof date === 'string' && date.includes('/')) {
        // Table row date is in "MM/DD/YYYY" format, convert to "YYYY/MM/DD"
        const [month, day, year] = date.split('/');
        return `${year}/${month}/${day}`;
      }
      return date || "2025/09/21";
    };

    // Mock data for export details - replace with actual API call
    const exportData = {
      date: formatDateForDisplay(selectedDate),
      dateExported: "15/05/2025, 17:48:30",
      totalFiles: "2",
      exportType: "By Date"
    };
    setExportDetailsData(exportData);
    setOpenExportDetailsModal(true);
  };

  const handleCloseImportDetailsModal = () => {
    setOpenImportDetailsModal(false);
    setImportDetailsData(null);
  };

  const handleCloseExportDetailsModal = () => {
    setOpenExportDetailsModal(false);
    setExportDetailsData(null);
  };

  const handleImportClick = () => {
    if (selectedRow || selectedCalendarDate) {
      // Pass the selected data with proper date formatting
      const dataToUse = selectedRow || { date: selectedCalendarDate };
      handleImportDetailsClick(dataToUse);
    }
  };

  const handleExportClick = () => {
    if (selectedRow || selectedCalendarDate) {
      // Pass the selected data with proper date formatting
      const dataToUse = selectedRow || { date: selectedCalendarDate };
      handleExportDetailsClick(dataToUse);
    }
  };

  const isFormValid = () => {
    switch (selectedDownloadOption) {
      case 'Download by Date':
        return selectedDate !== '';
      case 'Download by Time Period':
        return startDate !== '' && endDate !== '';
      case 'Download Specific Trades':
        return selectedTrades.length > 0; // At least one trade must be selected
      default:
        return false;
    }
  };

  // Filter orders based on selected criteria
  const filterOrders = () => {
   
    
    // Ensure ordersData is always an array
    const safeOrdersData = Array.isArray(ordersData) ? ordersData : [];
   
    let filtered = [...safeOrdersData];
    
    // Always exclude MANAGER_DISCRETION orders and filter for BUY/SELL actions only
    filtered = filtered.filter(order => 
      order.type !== 'MANAGER_DISCRETION' && 
      (order.action === 'BUY' || order.action === 'SELL')
    );


    switch (selectedDownloadOption) {
      case 'Download by Date':
       
        if (selectedDate) {
          const selectedDateObj = new Date(selectedDate);
          selectedDateObj.setHours(23, 59, 59, 999); // End of selected day
         
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.createdAt);
            const isIncluded = orderDate <= selectedDateObj;
           
            return isIncluded;
          });
         
        } else {
        
        }
        break;

      case 'Download by Time Period':
      
        if (startDate && endDate) {
          // Create date objects and set time to start/end of day for proper comparison
          const startDateObj = new Date(startDate);
          startDateObj.setHours(0, 0, 0, 0); // Start of day
          
          const endDateObj = new Date(endDate);
          endDateObj.setHours(23, 59, 59, 999); // End of day
         
          
          filtered = filtered.filter(order => {
            const orderDate = new Date(order.createdAt);
            const isIncluded = orderDate >= startDateObj && orderDate <= endDateObj;
           
            return isIncluded;
          });
        
        } else {
          
        }
        break;

      case 'Download by Trade Status':
       
        if (selectedTradeStatus) {
      
          filtered = filtered.filter(order => {
            const orderStatus = order.status?.toLowerCase();
            const selectedStatus = selectedTradeStatus.toLowerCase();
            const isIncluded = orderStatus === selectedStatus;
            return isIncluded;
          });
         
        } else {
         
        }
      
        break;

      case 'Download Specific Trades':
       
        // For specific trades, we'll use the selectedTrades array
        filtered = filtered.filter((order, index) => {
          const isIncluded = selectedTrades.includes(index);
         
          return isIncluded;
        });
       
        break;

      default:
       
        // Default: show all pending orders
        filtered = filtered.filter(order => {
          const isIncluded = order.status?.toLowerCase() === 'pending';
          
          return isIncluded;
        });
       
    }

    setFilteredOrders(filtered);
    return filtered;
  };

  // Transform order data for preview modal
  const transformOrdersForPreview = (orders) => {
    return orders?.map((order, index) => ({
      'Action': order.action,
      'Amount': order.amount,
      'Status': order.status,
      'Type': order.type,
      'Created At': new Date(order.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      'Strategy': order.strategy?.name || 'N/A',
      'Custodian': order.custodian || 'N/A',
      'Transactions': order.transactions?.length || 0,
      'Manager Discretion': order.isManagerDiscretion ? 'Yes' : 'No'
    }));
  };

  // Handle preview button click
  const handlePreviewClick = () => {
  

    let filtered = [];
    let transformedData = [];
    
    if (selectedDownloadOption === 'Download Specific Trades') {
      // For specific trades, we need to work with the CSV format data
      const safeOrdersData = Array.isArray(ordersData) ? ordersData : [];
      // Exclude MANAGER_DISCRETION orders first and filter for BUY/SELL actions only
      const nonManagerDiscretionOrders = safeOrdersData.filter(order => 
        order.type !== 'MANAGER_DISCRETION' && 
        (order.action === 'BUY' || order.action === 'SELL')
      );
      
    
      // For specific trades, we need to create a new array that only contains the selected records
      // We'll create a custom data structure that represents only the selected CSV records
      const selectedRecords = [];
      let csvRecordIndex = 0;
      
      nonManagerDiscretionOrders.forEach((order, orderIndex) => {
        const transactionCount = order.transactions?.length || 1; // At least 1 record per order
        
        for (let i = 0; i < transactionCount; i++) {
          if (selectedTrades.includes(csvRecordIndex)) {
            // This record is selected, add it to our filtered results
            selectedRecords.push({
              ...order,
              _selectedRecordIndex: csvRecordIndex,
              _transactionIndex: i
            });
          }
          csvRecordIndex++;
        }
      });
      
      
      // For the preview, we'll pass the selected records as a special format
      // that the PreviewModal can understand
      filtered = selectedRecords;
      transformedData = transformOrdersForPreview(selectedRecords);
    } else {
      // For other options, use the existing filter logic
      filtered = filterOrders();
      transformedData = transformOrdersForPreview(filtered);
    }
    
    // Create preview data in the format expected by PreviewModal
    // Pass the raw filtered orders data for CSV transformation
    const previewData = {
      success: true,
      data: filtered, // Pass raw order data instead of transformed data
      headers: ['Action', 'Amount', 'Status', 'Type', 'Created At', 'Strategy', 'Custodian', 'Transactions', 'Manager Discretion'],
      originalFormat: 'Orders API',
      fileName: selectedDownloadOption === 'Download Specific Trades' 
        ? `Selected Trades (${filtered.length})` 
        : `Filtered Orders (${filtered.length})`,
      rowCount: filtered.length,
      validation: {
        isValid: true,
        warnings: [],
        errors: []
      }
    };

  
    
    // Store the preview data for the modal and update states
    setFilteredOrders(transformedData);
    setPreviewData(previewData);
    setOpenDownloadModal(false);
    setOpenPreviewModal(true);

  };

  // Mock trade data for preview - updated to match exact image data
  const mockTradeData = [
    {
      investorEmail: { name: "Tahir Sultan", email: "tahirsultan2@gmail.com" },
      strategy: "AI Core",
      status: "Pending",
      registered: "17/08/2018, 17:48:30",
      amount: "12,000,000,300.00"
    },
    {
      investorEmail: { name: "Ibraheem Noor", email: "ibraheemnoor@gmail.com" },
      strategy: "Innovation Strategy",
      status: "Pending",
      registered: "17/08/2018, 17:48:30",
      amount: "12,000,000,300.00"
    },
    {
      investorEmail: { name: "Yusra Shahab", email: "yusrashahab@gmail.com" },
      strategy: "Anti-Innovation Strategy",
      status: "Pending",
      registered: "17/08/2018, 17:48:30",
      amount: "12,000,000,300.00"
    },
    {
      investorEmail: { name: "Naseer Ahmed", email: "naseerahmed@gmail.com" },
      strategy: "Anti-AI Core",
      status: "Pending",
      registered: "18/08/2018, 17:48:30",
      amount: "12,000,000,300.00"
    },
    {
      investorEmail: { name: "Rohit Maheshwari", email: "rohitmaheshwari@gmail.com" },
      strategy: "AI Innovation Strategy",
      status: "Pending",
      registered: "18/08/2018, 17:48:30",
      amount: "12,000,000,300.00"
    }
  ];

  const handleDownloadTemplate = () => {
    // Handle template download

  };

  const handleFileUpload = (uploadData) => {
    // Handle file upload with API response data
    if (uploadData) {
      try {
        // Show success modal with API response data
        setUploadResult({
          fileName: uploadData.fileName,
          originalFormat: uploadData.originalFormat,
          rowCount: uploadData.rowCount,
          headers: uploadData.headers,
          data: uploadData.data,
          apiResponse: uploadData.apiResponse
        });
        setShowSuccessModal(true);
        
        // Clear selected files and close upload modal
        setSelectedFiles([]);
        setOpenUploadModal(false);
        
      } catch (error) {
        // Handle upload error
        setUploadError({
          fileName: uploadData.fileName,
          error: error.message || 'Failed to process upload response',
          type: 'error'
        });
        setShowErrorModal(true);
      }
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    // Accept any files for now, but you can add validation later
    setSelectedFiles(prev => {
      const newFiles = [...prev, ...files];
      return newFiles;
    });
    // Clear the input value to allow selecting the same file again
    event.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setUploadResult(null);
  };

  const handleErrorModalClose = () => {
    setShowErrorModal(false);
    setUploadError(null);
  };

  const handleRetryUpload = () => {
    setShowErrorModal(false);
    setUploadError(null);
    // Optionally reopen the upload modal
    setOpenUploadModal(true);
  };

  // If a custodian is selected, show the detailed view
  if (selectedCustodian) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ minHeight: "100vh", backgroundColor: "#F5F5F5" }}>
          {/* Header Section */}
              <div style={{
            padding: "20px 24px", 
           
            borderBottom: "1px solid #E6E6E6",
                display: "flex",
                alignItems: "center",
            justifyContent: "space-between"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <IconButton onClick={handleBackClick} sx={{ p: 0 }}>
                <ArrowBackIcon sx={{ fontSize: 24, color: "#000" }} />
              </IconButton>
              <div>
                <Typography variant="h4" component="h1" sx={{ 
                  fontWeight: "bold", 
                  color: "#000", 
                    fontSize: "24px",
                  margin: 0,
                  lineHeight: "1.2"
                }}>
                  Charles Schwab
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: "#666", 
                    fontSize: "14px",
                  margin: 0,
                  marginTop: "4px"
                }}>
                  Manage your lots import and trade exports here
                </Typography>
                </div>
            </div>
            
            <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="contained"
                  startIcon={<KeyboardArrowUpIcon />}
                  onClick={() => handleImport(selectedCustodian)}
                  sx={{
                  height: "32px",
                  padding: "0 12px",
                    borderRadius: "4px",
                    background: "#1344EB",
                    color: "white",
                    textTransform: "none",
                    fontWeight: "500",
                  fontSize: "12px",
                  minWidth: "100px",
                    boxShadow: "none",
                    "&:hover": {
                      background: "#0f3ad1",
                      boxShadow: "none"
                    }
                  }}
                >
                New Import
                </Button>
                <Button
                  variant="contained"
                  startIcon={<KeyboardArrowDownIcon />}
                  onClick={() => handleExport(selectedCustodian)}
                  sx={{
                  height: "32px",
                  padding: "0 12px",
                    borderRadius: "4px",
                    background: "#1344EB",
                    color: "white",
                    textTransform: "none",
                    fontWeight: "500",
                  fontSize: "12px",
                  minWidth: "100px",
                    boxShadow: "none",
                    "&:hover": {
                      background: "#0f3ad1",
                      boxShadow: "none"
                    }
                  }}
                >
                New Export
                </Button>
              </div>
              </div>
              
          <Container
            maxWidth="lg"
            component="main"
            style={{ padding: "24px", position: "relative" }}
          >
            {/* Coming Soon Watermark Overlay - Only for this section */}
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.92)",
              backdropFilter: "blur(3px)",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "all",
              borderRadius: "8px"
            }}>
              <div style={{
                textAlign: "center",
                padding: "48px",
                backgroundColor: "white",
                borderRadius: "16px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "2px solid #E6E6E6",
                maxWidth: "450px"
              }}>
                <div style={{
                  fontSize: "56px",
                  marginBottom: "16px",
                  opacity: 0.7
                }}>
                  📊
                </div>
                <Typography
                  variant="h3"
                  style={{
                    fontSize: "28px",
                    fontWeight: "700",
                    color: "#18275A",
                    marginBottom: "10px",
                    letterSpacing: "-0.5px"
                  }}
                >
                  Coming Soon
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    fontSize: "15px",
                    color: "#666666",
                    lineHeight: "1.6",
                    marginBottom: "6px"
                  }}
                >
                  Progress tracking and status monitoring will be available soon.
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontSize: "13px",
                    color: "#999999",
                    fontStyle: "italic"
                  }}
                >
                  Thank you for your patience!
                </Typography>
              </div>
            </div>
            
            {/* Navigation Tabs and Date Filters Card */}
              <div style={{
              backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                border: "1px solid #E6E6E6",
              padding: "16px 20px",
              marginBottom: "20px"
            }}>
              {/* Navigation Tabs */}
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "16px"
              }}>
                <div style={{ display: "flex", gap: "24px", alignItems: "center" }}>
                  <div 
                    style={{
                      paddingBottom: "8px",
                      borderBottom: activeTab === 'list' ? "2px solid #1344EB" : "none",
                      color: activeTab === 'list' ? "#1344EB" : "#000",
                      fontWeight: activeTab === 'list' ? "500" : "400",
                      fontSize: "14px",
                      cursor: "pointer"
                    }}
                    onClick={() => setActiveTab('list')}
                  >
                    List View
                  </div>
                  <div 
                    style={{
                      paddingBottom: "8px",
                      borderBottom: activeTab === 'calendar' ? "2px solid #1344EB" : "none",
                      color: activeTab === 'calendar' ? "#1344EB" : "#000",
                      fontWeight: activeTab === 'calendar' ? "500" : "400",
                      fontSize: "14px",
                      cursor: "pointer"
                    }}
                    onClick={() => setActiveTab('calendar')}
                  >
                    Calendar View
                  </div>
                </div>
              </div>

              {/* Date Range Filter Buttons */}
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  backgroundColor: "#1344EB",
                  color: "white",
                  fontWeight: "500",
                  fontSize: "12px",
                  cursor: "pointer"
                }}>
                  All
                </div>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#000",
                  fontWeight: "400",
                  fontSize: "12px",
                  cursor: "pointer",
                  border: "1px solid #E6E6E6"
                }}>
                  Last 14 Days
                </div>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#000",
                  fontWeight: "400",
                  fontSize: "12px",
                  cursor: "pointer",
                  border: "1px solid #E6E6E6"
                }}>
                  Last 7 Days
                </div>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#000",
                  fontWeight: "400",
                  fontSize: "12px",
                  cursor: "pointer",
                  border: "1px solid #E6E6E6"
                }}>
                  Last 30 Days
                </div>
                <div style={{
                  padding: "6px 12px",
                  borderRadius: "4px",
                  backgroundColor: "transparent",
                  color: "#000",
                  fontWeight: "400",
                  fontSize: "12px",
                  cursor: "pointer",
                  border: "1px solid #E6E6E6"
                }}>
                  Last 90 Days
                </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'list' ? (
              <>

                {/* Search and Filter Bar with Data Table Card */}
                <div style={{
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #E6E6E6",
                  marginBottom: "20px"
                }}>
                  {/* Search and Filter Bar */}
                  <div style={{
                    padding: "16px 20px",
                    borderBottom: "1px solid #E6E6E6",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px"
                  }}>
                    <div style={{ position: "relative", flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Search"
                        style={{
                          width: "100%",
                          padding: "8px 12px 8px 32px",
                          border: "1px solid #E6E6E6",
                          borderRadius: "4px",
                          fontSize: "14px",
                          outline: "none"
                        }}
                      />
                      <div style={{
                        position: "absolute",
                        left: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#666",
                        fontSize: "16px"
                      }}>
                        🔍
                      </div>
                    </div>
                    
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "12px", color: "#666" }}>Filter by Date Range</span>
                      <div style={{
                        width: "16px",
                        height: "16px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <div style={{
                          width: "12px",
                          height: "8px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "1px"
                        }}>
                          <div style={{ width: "100%", height: "1px", backgroundColor: "#666" }}></div>
                          <div style={{ width: "80%", height: "1px", backgroundColor: "#666" }}></div>
                          <div style={{ width: "60%", height: "1px", backgroundColor: "#666" }}></div>
                        </div>
                      </div>
                    </div>
                    
                    <input
                      type="text"
                      placeholder="MM/DD/YYYY"
                      style={{
                        width: "120px",
                        padding: "8px 12px",
                        border: "1px solid #E6E6E6",
                        borderRadius: "4px",
                        fontSize: "12px",
                        outline: "none"
                      }}
                    />
                    
                    <input
                      type="text"
                      placeholder="MM/DD/YYYY"
                      style={{
                        width: "120px",
                        padding: "8px 12px",
                        border: "1px solid #E6E6E6",
                        borderRadius: "4px",
                        fontSize: "12px",
                        outline: "none"
                      }}
                    />
                    
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span style={{ fontSize: "12px", color: "#666" }}>Sort</span>
                      <div style={{
                        width: "12px",
                        height: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}>
                        <div style={{
                          width: "0",
                          height: "0",
                          borderLeft: "4px solid transparent",
                          borderRight: "4px solid transparent",
                          borderTop: "6px solid #666"
                        }}></div>
                      </div>
                    </div>
                  </div>

                  {/* Import/Export Buttons - Only show when row is selected */}
                  {(selectedRow || selectedCalendarDate) && (
                    <div style={{
                      padding: "16px 20px",
                      borderBottom: "1px solid #E6E6E6",
                      display: "flex",
                      gap: "12px",
                      alignItems: "center"
                    }}>
                      <Button
                        variant="contained"
                        startIcon={<KeyboardArrowUpIcon />}
                        onClick={handleImportClick}
                        sx={{
                          height: "32px",
                          padding: "0 16px",
                          borderRadius: "4px",
                          background: "#1344EB",
                          color: "white",
                          textTransform: "none",
                          fontWeight: "500",
                          fontSize: "12px",
                          minWidth: "100px",
                          boxShadow: "none",
                          "&:hover": {
                            background: "#0f3ad1",
                            boxShadow: "none"
                          }
                        }}
                      >
                        Import
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={handleExportClick}
                        sx={{
                          height: "32px",
                          padding: "0 16px",
                          borderRadius: "4px",
                          background: "#1344EB",
                          color: "white",
                          textTransform: "none",
                          fontWeight: "500",
                          fontSize: "12px",
                          minWidth: "100px",
                          boxShadow: "none",
                          "&:hover": {
                            background: "#0f3ad1",
                            boxShadow: "none"
                          }
                        }}
                      >
                        Export
                      </Button>
                      <div style={{ fontSize: "12px", color: "#666", marginLeft: "8px" }}>
                        {selectedRow ? `Selected: ${selectedRow.date}` : `Selected: ${selectedCalendarDate}`}
                      </div>
                    </div>
                  )}

                  {/* Data Table */}
                  {/* Table Header */}
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                    gap: "16px",
                    padding: "16px 20px",
                    backgroundColor: "#F8F9FA",
                    borderBottom: "1px solid #E6E6E6",
                    fontWeight: "600",
                    fontSize: "12px",
                    color: "#333"
                  }}>
                    <div>Date</div>
                    <div>Import Status</div>
                    <div>Import Date & Time</div>
                    <div>Export Status</div>
                    <div>Export Date & Time</div>
                    <div>Actions</div>
                  </div>

                  {/* Table Rows */}
                  <div 
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                      gap: "16px",
                      padding: "16px 20px",
                      borderBottom: "1px solid #F0F0F0",
                      fontSize: "12px",
                      color: "#333",
                      cursor: "pointer",
                      backgroundColor: selectedRow?.date === "09/25/2025" ? "#F0F8FF" : "transparent",
                      border: selectedRow?.date === "09/25/2025" ? "2px solid #1344EB" : "none"
                    }}
                    onClick={() => handleRowSelection({ date: "09/25/2025", importStatus: "Done", exportStatus: "Done" })}
                  >
                    <div>09/25/2025</div>
                    <div>
                      <span style={{
                        backgroundColor: "#E8F5E8",
                        color: "#4CAF50",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Done
                      </span>
                    </div>
                    <div>09/30/2025, 17:48:30</div>
                    <div>
                      <span style={{
                        backgroundColor: "#E8F5E8",
                        color: "#4CAF50",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Done
                      </span>
                    </div>
                    <div>09/30/2025, 17:48:30</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span 
                        style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImportDetailsClick({ date: "09/25/2025" });
                        }}
                      >
                        Import Details
                      </span>
                      <span 
                        style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportDetailsClick({ date: "09/25/2025" });
                        }}
                      >
                        Export Details
                      </span>
                    </div>
                  </div>

                  <div 
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                      gap: "16px",
                      padding: "16px 20px",
                      borderBottom: "1px solid #F0F0F0",
                      fontSize: "12px",
                      color: "#333",
                      cursor: "pointer",
                      backgroundColor: selectedRow?.date === "09/24/2025" ? "#F0F8FF" : "transparent",
                      border: selectedRow?.date === "09/24/2025" ? "2px solid #1344EB" : "none"
                    }}
                    onClick={() => handleRowSelection({ date: "09/24/2025", importStatus: "Done", exportStatus: "Missing" })}
                  >
                    <div>09/24/2025</div>
                    <div>
                      <span style={{
                        backgroundColor: "#E8F5E8",
                        color: "#4CAF50",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Done
                      </span>
                    </div>
                    <div>09/30/2025, 17:48:30</div>
                    <div>
                      <span style={{
                        backgroundColor: "#FFEBEE",
                        color: "#F44336",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Missing
                      </span>
                    </div>
                    <div>-</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span 
                        style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImportDetailsClick({ date: "09/24/2025" });
                        }}
                      >
                        Import Details
                      </span>
                      <span 
                        style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportClick();
                        }}
                      >
                        Export
                      </span>
                    </div>
                  </div>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr 1fr",
                    gap: "16px",
                    padding: "16px 20px",
                    borderBottom: "1px solid #F0F0F0",
                    fontSize: "12px",
                    color: "#333"
                  }}>
                    <div>09/23/2025</div>
                    <div>
                      <span style={{
                        backgroundColor: "#FFEBEE",
                        color: "#F44336",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Missing
                      </span>
                    </div>
                    <div>-</div>
                    <div>
                      <span style={{
                        backgroundColor: "#E8F5E8",
                        color: "#4CAF50",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: "500"
                      }}>
                        Done
                      </span>
                    </div>
                    <div>09/30/2025, 17:48:30</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <span style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}>Import</span>
                      <span style={{ color: "#1344EB", cursor: "pointer", fontSize: "11px" }}>Export Details</span>
                    </div>
                  </div>
                </div>

                {/* Pagination */}
                <div style={{
                  display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                  padding: "0 4px"
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "#666" }}>Rows per page:</span>
                    <div style={{ position: "relative" }}>
                      <select style={{
                        padding: "4px 20px 4px 8px",
                        border: "1px solid #E6E6E6",
                        borderRadius: "4px",
                        fontSize: "12px",
                        appearance: "none",
                        backgroundColor: "white",
                        cursor: "pointer"
                      }}>
                        <option>7</option>
                      </select>
                      <div style={{
                        position: "absolute",
                        right: "6px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none"
                      }}>
                        <div style={{
                          width: "0",
                          height: "0",
                          borderLeft: "4px solid transparent",
                          borderRight: "4px solid transparent",
                          borderTop: "4px solid #666"
                        }}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button style={{
                      padding: "4px 8px",
                      border: "1px solid #E6E6E6",
                      backgroundColor: "white",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}>
                      ←
                    </button>
                    <button style={{
                      padding: "4px 8px",
                      border: "1px solid #1344EB",
                      backgroundColor: "#1344EB",
                      color: "white",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}>
                      1
                    </button>
                    <button style={{
                      padding: "4px 8px",
                      border: "1px solid #E6E6E6",
                      backgroundColor: "white",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}>
                      →
                    </button>
                  </div>
                </div>
                  </>
            ) : (
              /* Calendar View */
              <div style={{ display: "flex", gap: "24px" }}>
                {/* Import Lots Calendar Card */}
                <div style={{
                  flex: "1",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "8px",
                  border: "1px solid #E6E6E6",
                  padding: "24px",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <h2 style={{
                    color: "#141218",
                    fontFamily: "Founders Grotesk, -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: "24px",
                    fontWeight: "500",
                    lineHeight: "32px",
                    margin: "0 0 8px 0"
                  }}>
                        Import Lots
                  </h2>
                  <p style={{
                    color: "#545454",
                    fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, sans-serif",
                    fontSize: "14px",
                    fontWeight: "400",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                        margin: "0 0 20px 0"
                      }}>
                        Upload file(s) with lots
                      </p>
                      
                      {/* Calendar Component */}
                      <div style={{ marginBottom: "20px" }}>
                        {/* Calendar Header */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "16px"
                        }}>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>←</div>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <select style={{
                              border: "none",
                              fontSize: "16px",
                              fontWeight: "500",
                              outline: "none",
                              backgroundColor: "transparent"
                            }}>
                              <option>August</option>
                            </select>
                            <select style={{
                              border: "none",
                              fontSize: "16px",
                              fontWeight: "500",
                              outline: "none",
                              backgroundColor: "transparent"
                            }}>
                              <option>2025</option>
                            </select>
                </div>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>→</div>
                        </div>
                        
                        {/* Calendar Days */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                          gap: "8px",
                          textAlign: "center"
                        }}>
                          {/* Days of week */}
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                            <div key={index} style={{
                              fontSize: "12px",
                              color: "#999",
                              padding: "8px 0"
                            }}>
                              {day}
                            </div>
                          ))}
                          
                          {/* Calendar dates */}
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => {
                            let circleStyle = {};
                            if ([2, 3, 4].includes(date)) {
                              circleStyle = {
                                backgroundColor: "#4CAF50",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else if ([7, 8, 9, 10, 11, 14, 15, 16].includes(date)) {
                              circleStyle = {
                                border: "2px solid #F44336",
                                color: "#F44336",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else if (date === 17) {
                              circleStyle = {
                                backgroundColor: "#1344EB",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else {
                              circleStyle = {
                                padding: "8px 0",
                                color: "#999"
                              };
                            }
                            
                            return (
                              <div 
                                key={date} 
                                style={{
                                  ...circleStyle,
                                  cursor: "pointer",
                                  backgroundColor: selectedCalendarDate === date ? "#1344EB" : circleStyle.backgroundColor,
                                  color: selectedCalendarDate === date ? "white" : circleStyle.color,
                                  border: selectedCalendarDate === date ? "2px solid #1344EB" : circleStyle.border
                                }}
                                onClick={() => handleCalendarDateSelection(date)}
                              >
                                {circleStyle.width ? date : date}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                <Button
                  variant="contained"
                  startIcon={<KeyboardArrowUpIcon />}
                  onClick={() => handleImportClick()}
                  disabled={!selectedCalendarDate}
                  sx={{
                    height: "40px",
                    padding: "0 16px",
                    borderRadius: "4px",
                    background: selectedCalendarDate ? "#1344EB" : "#CCCCCC",
                    color: "white",
                    textTransform: "none",
                    fontWeight: "500",
                    minWidth: "120px",
                    boxShadow: "none",
                    "&:hover": {
                      background: selectedCalendarDate ? "#0f3ad1" : "#CCCCCC",
                      boxShadow: "none"
                    },
                    "&:disabled": {
                      background: "#CCCCCC",
                      color: "#999999"
                    }
                  }}
                >
                  Import Details
                </Button>
            </div>
            
                    {/* Export Trades Calendar Card */}
            <div style={{
                      flex: "1",
                      backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              border: "1px solid #E6E6E6",
              padding: "24px",
              display: "flex",
                      flexDirection: "column"
            }}>
                <h2 style={{
                  color: "#141218",
                  fontFamily: "Founders Grotesk, -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: "24px",
                  fontWeight: "500",
                  lineHeight: "32px",
                  margin: "0 0 8px 0"
                }}>
                        Export Trades
                </h2>
                <p style={{
                  color: "#545454",
                  fontFamily: "Roboto, -apple-system, BlinkMacSystemFont, sans-serif",
                  fontSize: "14px",
                  fontWeight: "400",
                  lineHeight: "20px",
                  letterSpacing: "0.25px",
                        margin: "0 0 20px 0"
                      }}>
                        Download a list of all trades
                      </p>
                      
                      {/* Calendar Component */}
                      <div style={{ marginBottom: "20px" }}>
                        {/* Calendar Header */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "16px"
                        }}>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>←</div>
                          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                            <select style={{
                              border: "none",
                              fontSize: "16px",
                              fontWeight: "500",
                              outline: "none",
                              backgroundColor: "transparent"
                            }}>
                              <option>August</option>
                            </select>
                            <select style={{
                              border: "none",
                              fontSize: "16px",
                              fontWeight: "500",
                              outline: "none",
                              backgroundColor: "transparent"
                            }}>
                              <option>2025</option>
                            </select>
              </div>
                          <div style={{ fontSize: "16px", fontWeight: "500" }}>→</div>
                        </div>
                        
                        {/* Calendar Days */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr)",
                          gap: "8px",
                          textAlign: "center"
                        }}>
                          {/* Days of week */}
                          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                            <div key={index} style={{
                              fontSize: "12px",
                              color: "#999",
                              padding: "8px 0"
                            }}>
                              {day}
                            </div>
                          ))}
                          
                          {/* Calendar dates */}
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => {
                            let circleStyle = {};
                            if ([2, 3, 4].includes(date)) {
                              circleStyle = {
                                backgroundColor: "#4CAF50",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else if ([7, 8, 9, 10, 11, 14, 15, 16].includes(date)) {
                              circleStyle = {
                                border: "2px solid #F44336",
                                color: "#F44336",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else if (date === 17) {
                              circleStyle = {
                                backgroundColor: "#1344EB",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto"
                              };
                            } else {
                              circleStyle = {
                                padding: "8px 0",
                                color: "#999"
                              };
                            }
                            
                            return (
                              <div 
                                key={date} 
                                style={{
                                  ...circleStyle,
                                  cursor: "pointer",
                                  backgroundColor: selectedCalendarDate === date ? "#1344EB" : circleStyle.backgroundColor,
                                  color: selectedCalendarDate === date ? "white" : circleStyle.color,
                                  border: selectedCalendarDate === date ? "2px solid #1344EB" : circleStyle.border
                                }}
                                onClick={() => handleCalendarDateSelection(date)}
                              >
                                {circleStyle.width ? date : date}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      
                      <Button
                        variant="contained"
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={() => handleExportClick()}
                        disabled={!selectedCalendarDate}
                        sx={{
                          height: "40px",
                          padding: "0 16px",
                          borderRadius: "4px",
                          background: selectedCalendarDate ? "#1344EB" : "#CCCCCC",
                          color: "white",
                          textTransform: "none",
                          fontWeight: "500",
                          minWidth: "120px",
                          boxShadow: "none",
                          "&:hover": {
                            background: selectedCalendarDate ? "#0f3ad1" : "#CCCCCC",
                            boxShadow: "none"
                          },
                          "&:disabled": {
                            background: "#CCCCCC",
                            color: "#999999"
                          }
                        }}
                      >
                        Export
                      </Button>
            </div>
                  </div>
                )}
          </Container>
          
          {/* Upload Modal */}
          <UploadModal 
            open={openUploadModal}
            onClose={handleCloseUploadModal}
            selectedFiles={selectedFiles}
            onFileSelect={handleFileSelect}
            onFileUpload={handleFileUpload}
            onDownloadTemplate={handleDownloadTemplate}
            onRemoveFile={removeFile}
            onError={(errorData) => {
              setUploadError(errorData);
              setShowErrorModal(true);
            }}
          />
          {/* Download Modal */}
          <DownloadModal 
            open={openDownloadModal}
            onClose={handleCloseDownloadModal}
            selectedDownloadOption={selectedDownloadOption}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            selectedTradeStatus={selectedTradeStatus}
            onDownloadOptionSelect={handleDownloadOptionSelect}
            onDateChange={setSelectedDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onTradeStatusChange={setSelectedTradeStatus}
            onNextPreview={handlePreviewClick}
            isFormValid={isFormValid}
            isLoading={isLoadingOrders}
            ordersCount={Array.isArray(ordersData) ? ordersData.length : 0}
          />
          {/* Preview Modal */}
          <PreviewModal 
            open={openPreviewModal}
            onClose={handleClosePreviewModal}
            onBack={handleBackToDownload}
            parsedData={previewData || {
              success: true,
              data: [],
              headers: ['Action', 'Amount', 'Status', 'Type', 'Created At', 'Strategy', 'Custodian', 'Transactions', 'Manager Discretion'],
              originalFormat: 'Orders API',
              fileName: 'No Orders Found',
              rowCount: 0,
              validation: {
                isValid: true,
                warnings: [],
                errors: []
              }
            }}
            strategiesData={strategiesWithInvestors || []}
          />
          {/* Select Trades Modal */}
          <SelectTradesModal 
            open={openSelectTradesModal}
            onClose={handleCloseSelectTradesModal}
            onBack={() => {
              setOpenSelectTradesModal(false);
              setOpenDownloadModal(true);
            }}
            onNext={() => {
              setOpenSelectTradesModal(false);
              handlePreviewClick();
            }}
            selectedTrades={selectedTrades}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onTradeSelection={handleTradeSelection}
            onSelectAllTrades={handleSelectAllTrades}
            mockTradeData={Array.isArray(ordersData) && ordersData.length > 0 ? ordersData.filter(order => 
              order.type !== 'MANAGER_DISCRETION' && 
              (order.action === 'BUY' || order.action === 'SELL')
            ) : []}
            strategiesData={strategiesWithInvestors || []}
          />
          
          {/* Success Modal */}
          <UploadSuccessModal
            open={showSuccessModal}
            onClose={handleSuccessModalClose}
            uploadData={uploadResult}
          />
          
          {/* Error Modal */}
          <UploadErrorModal
            open={showErrorModal}
            onClose={handleErrorModalClose}
            onRetry={handleRetryUpload}
            errorData={uploadError}
          />

          {/* Import Details Modal */}
          {openImportDetailsModal && importDetailsData && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px"
                }}>
                  <h2 style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#333"
                  }}>
                    Import Details
                  </h2>
                  <button
                    onClick={handleCloseImportDetailsModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#666"
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <p style={{
                  fontSize: "14px",
                  color: "#666",
                  margin: "0 0 20px 0"
                }}>
                  See import details for <strong>{importDetailsData.date}</strong> under:
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500" }}>Date imported:</span>
                    <span>{importDetailsData.dateImported}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500" }}>Total Imported File(s):</span>
                    <span>{importDetailsData.totalFiles}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: "500" }}>Imported File type(s):</span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {importDetailsData.fileTypes.map((type, index) => (
                        <span key={index} style={{
                          backgroundColor: "#E8F5E8",
                          color: "#4CAF50",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          border: "1px solid #4CAF50"
                        }}>
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: "500" }}>Missing Files(s):</span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      {importDetailsData.missingFiles.map((file, index) => (
                        <span key={index} style={{
                          backgroundColor: "#E8F5E8",
                          color: "#4CAF50",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          border: "1px solid #4CAF50"
                        }}>
                          {file}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export Details Modal */}
          {openExportDetailsModal && exportDetailsData && (
            <div style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000
            }}>
              <div style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "400px",
                maxWidth: "500px",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)"
              }}>
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px"
                }}>
                  <h2 style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#333"
                  }}>
                    Export Details
                  </h2>
                  <button
                    onClick={handleCloseExportDetailsModal}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#666"
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <p style={{
                  fontSize: "14px",
                  color: "#666",
                  margin: "0 0 20px 0"
                }}>
                  See export details for <strong>{exportDetailsData.date}</strong> under:
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500" }}>Date Exported:</span>
                    <span>{exportDetailsData.dateExported}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500" }}>Total Exported File(s):</span>
                    <span>{exportDetailsData.totalFiles}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: "500" }}>Export Type:</span>
                    <span>{exportDetailsData.exportType}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ThemeProvider>
    );
  }

  // Main custodian list view
  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <div style={{ minHeight: "100vh", backgroundColor: "#F5F5F5" }}>
          <div style={{ padding: "20px 24px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #E6E6E6" }}>
              <Typography 
                variant="h4" 
                component="h1"
                style={{ 
                  fontSize: "24px", 
                  color: "#18275A", 
                  fontWeight: "700", 
                  margin: "0 0 8px 0",
                  lineHeight: "1.2"
                }}
              >
                Custodian Management
              </Typography>
              <Typography 
                variant="body1"
                style={{ 
                  fontSize: "14px", 
                  color: "#666666", 
                  margin: "0", 
                  fontWeight: "400",
                  lineHeight: "1.4"
                }}
              >
                Lorem ipsum dolor sit amet consectetur. Accumsan eget est.
              </Typography>
            </div>
          
        {loading ? (
          <Loader />
        ) : (
          <Container
            maxWidth="lg"
            component="main"
            style={{ 
              marginLeft: "0", 
                padding: "24px"
            }}
          >
            {CustodianData.length === 0 ? (
              <Typography
                variant="h6"
                style={{ textAlign: "center", marginTop: "20px" }}
              >
                Sorry, no data exists.
              </Typography>
            ) : (
              <div
                style={{
                  height: "calc(100vh - 200px)",
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingBottom: "20px"
                }}
              >
                <Grid container spacing={3}>
                  {CustodianData?.map((values) => (
                    <Grid item key={values.id} xs={12} sm={6} md={4}> 
                      <Card
                        sx={{
                          mt: 2,
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          minHeight: "80px",
                          height: "80px",
                          padding: "16px 20px",
                          borderRadius: "8px",
                          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                          cursor: "pointer",
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #F0F0F0",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                            transform: "translateY(-2px)",
                            borderColor: "#E0E0E0"
                          },
                        }}
                        className="Maincustcard"
                        onClick={() => handleCustodianClick(values)}
                      >
                        <CardContent
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "100%",
                            padding: "0",
                            margin: "0",
                            "&:last-child": { paddingBottom: "0" }
                          }}
                        >
                          <div style={{ 
                            width: "40px", 
                            height: "40px", 
                            marginRight: "16px", 
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "6px",
                            backgroundColor: "#F8F9FA",
                            border: "1px solid #E9ECEF"
                          }}>
                            <img
                              src={values?.logo}
                              alt={values?.custodianName}
                              style={{
                                width: "32px",
                                height: "32px",
                                objectFit: "contain",
                                maxWidth: "100%",
                                maxHeight: "100%"
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            {/* Fallback icon if image fails to load */}
                            <div style={{
                              display: 'none',
                              width: "32px",
                              height: "32px",
                              backgroundColor: "#E9ECEF",
                              borderRadius: "4px",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#6C757D"
                            }}>
                              {values?.name?.charAt(0) || 'C'}
                            </div>
                          </div>

                          <div style={{ 
                            flex: 1,
                            display: "flex",
                            alignItems: "center"
                          }}>
                            <Typography
                              variant="h6"
                              component="h2"
                              style={{ 
                                fontSize: "16px", 
                                fontWeight: "600",
                                color: "#333333",
                                margin: "0",
                                lineHeight: "1.2"
                              }}
                            >
                              {values?.name || values?.custodianName || 'Unknown Custodian'}
                            </Typography>
                          </div>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </Container>
        )}
        </div>
      </ThemeProvider>
    </>
  );
}

export default Index;
